import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../components/UI/Button/Button';
import { evaluateBenchmarkQuestions, getJobStatus } from '../../../../api/benchmarkApi';

interface RunBenchmarkEvaluationsProps {
  loading: boolean;
  setError: (error: string | null) => void;
}

const POLL_INTERVAL = 10000; // Poll every 10 seconds

const RunBenchmarkEvaluations: React.FC<RunBenchmarkEvaluationsProps> = ({ loading, setError }) => {
  const navigate = useNavigate();
  const [isEvaluating, setIsEvaluating] = useState(false);

  const pollJobStatus = useCallback(
    async (jobId: string, triggerId: string): Promise<void> => {
      try {
        const status = await getJobStatus(jobId);

        if (status.isFailed) {
          setError('Benchmark evaluation failed');
          setIsEvaluating(false);
          return;
        }

        if (status.isCompleted) {
          navigate(`/admin/evals?trigger_type=manual&trigger_id=${triggerId}`);
          return;
        }

        // Continue polling if job is still running
        setTimeout(() => pollJobStatus(jobId, triggerId), POLL_INTERVAL);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to check job status');
        setIsEvaluating(false);
      }
    },
    [navigate, setError],
  );

  const handleEvaluate = async () => {
    try {
      setIsEvaluating(true);
      setError(null);
      const { triggerId, jobId } = await evaluateBenchmarkQuestions();
      pollJobStatus(jobId, triggerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during evaluation');
      setIsEvaluating(false);
    }
  };

  return (
    <Button onClick={handleEvaluate} disabled={isEvaluating || loading} variant="secondary">
      {isEvaluating ? 'Running Evaluations...' : 'Run Evals'}
    </Button>
  );
};

export default RunBenchmarkEvaluations;
